/**
 * Checkout page with Stripe integration
 * Follows the unified UX pattern with modern design
 */

import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe, StripeElementsOptions } from '@stripe/stripe-js';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { useCurrency } from '../contexts/CurrencyContext';
import { api } from '../services/api';
import PaymentForm from '../components/checkout/PaymentForm';
import LegalDisclaimer from '../components/common/LegalDisclaimer';
import {
  CreditCard,
  ShoppingBag,
  Package,
  AlertTriangle,
  RefreshCw,
  ArrowLeft,
  HelpCircle,
  Shield,
  CheckCircle,
  AlertCircle,
} from 'lucide-react';

// Initialize Stripe
const stripePromise = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY
  ? loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY)
  : null;

export default function CheckoutPage(): JSX.Element {
  const { items, getTotal, getFormattedTotal, clearCart } = useCart();
  const { currentUser, userData } = useAuth();
  const { currency, formatPrice } = useCurrency();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [clientSecret, setClientSecret] = useState<string>('');
  const [orderId, setOrderId] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');

  const total = getTotal();
  // Use selected currency for checkout (lowercase for Stripe)
  const stripeCurrency = currency.toLowerCase();

  // Check Stripe configuration
  const stripeConfigured = !!import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY &&
    !import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY.includes('REPLACE');

  // Handle return from Stripe redirect (3D Secure, etc.)
  useEffect(() => {
    const paymentIntent = searchParams.get('payment_intent');
    const redirectStatus = searchParams.get('redirect_status');

    if (paymentIntent && redirectStatus === 'succeeded') {
      // Payment succeeded after redirect - clear cart and redirect to orders
      console.log('Detected Stripe redirect return with success');
      clearCart();
      window.location.href = `/user?tab=orders&success=true`;
    }
  }, [searchParams, clearCart]);

  useEffect(() => {
    // Redirect if cart is empty
    if (items.length === 0) {
      navigate('/cart');
      return;
    }

    // Create order and get payment intent
    const createOrder = async () => {
      if (!currentUser || !stripeConfigured) return;

      setLoading(true);
      setError('');

      try {
        const token = await currentUser.getIdToken();
        const response = await api.post(
          '/orders',
          {
            items: items.map(item => ({
              productId: item.productId,
              quantity: item.quantity,
            })),
            paymentProvider: 'stripe',
            currency: stripeCurrency,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        setClientSecret(response.data.clientSecret);
        setOrderId(response.data.orderId);
      } catch (err) {
        console.error('Error creating order:', err);
        setError('Failed to initialize payment. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    createOrder();
  }, [items, currentUser, navigate, stripeConfigured, stripeCurrency]);

  const handlePaymentSuccess = () => {
    console.log('Payment success handler called, orderId:', orderId);
    // Clear cart
    clearCart();

    // Use window.location for more reliable redirect
    window.location.href = `/user?tab=orders&order=${orderId}&success=true`;
  };

  const handlePaymentError = (errorMessage: string) => {
    setError(errorMessage);
  };

  if (items.length === 0) {
    return <></>;
  }

  if (!stripeConfigured) {
    return (
      <div className="container-custom py-12">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-gray-100">
              Checkout
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-2">
              Complete your purchase securely
            </p>
          </div>

          {/* Stripe Not Configured Error */}
          <div className="card p-8">
            <div className="text-center mb-6">
              <div className="p-4 bg-red-100 dark:bg-red-900/30 rounded-full inline-block mb-4">
                <AlertTriangle size={48} className="text-red-600 dark:text-red-400" />
              </div>
              <h2 className="text-2xl font-bold text-red-600 dark:text-red-400 mb-2">
                Payment Not Available
              </h2>
              <p className="text-gray-600 dark:text-gray-400">
                Payment processing is not currently configured.
              </p>
            </div>

            <div className="bg-gray-100 dark:bg-gray-800 rounded-xl p-6 mb-6">
              <h3 className="font-bold text-lg mb-3 flex items-center gap-2">
                <CreditCard size={18} />
                Setup Instructions
              </h3>
              <ol className="space-y-2 text-sm list-decimal list-inside text-gray-600 dark:text-gray-400">
                <li>Sign up for a Stripe account at <a href="https://stripe.com" target="_blank" rel="noopener noreferrer" className="text-primary-600 dark:text-primary-400 hover:underline">stripe.com</a></li>
                <li>Get your publishable key from the Stripe Dashboard</li>
                <li>Add it to your environment configuration</li>
              </ol>
            </div>

            <button onClick={() => navigate('/cart')} className="btn btn-secondary w-full inline-flex items-center justify-center gap-2">
              <ArrowLeft size={16} />
              Back to Cart
            </button>
          </div>
        </div>
      </div>
    );
  }

  const elementsOptions: StripeElementsOptions = {
    clientSecret,
    appearance: {
      theme: 'stripe',
      variables: {
        colorPrimary: '#1e40af',
      },
    },
    loader: 'auto',
  };

  return (
    <div className="container-custom py-12">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <Link
            to="/cart"
            className="inline-flex items-center gap-2 text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium mb-4"
          >
            <ArrowLeft size={16} />
            Back to Cart
          </Link>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-gray-100">
            Checkout
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            Complete your purchase securely
          </p>
        </div>

        {/* Security Badge */}
        <div className="flex items-center gap-3 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl mb-6">
          <Shield size={20} className="text-green-600 dark:text-green-400" />
          <div>
            <p className="text-sm font-medium text-green-800 dark:text-green-200">
              Secure Checkout
            </p>
            <p className="text-xs text-green-700 dark:text-green-300">
              Your payment is protected with SSL encryption
            </p>
          </div>
        </div>

        {/* Order Summary */}
        <div className="card p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
            <ShoppingBag size={20} />
            Order Summary
          </h2>

          <div className="space-y-4 mb-6">
            {items.map((item) => (
              <div key={item.productId} className="flex gap-4 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <div className="flex-shrink-0">
                  <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
                    <Package size={20} className="text-primary-600 dark:text-primary-400" />
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 dark:text-gray-100">{item.product?.title}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Qty: {item.quantity} • Digital Download
                  </p>
                </div>
                <span className="font-bold text-gray-900 dark:text-gray-100">
                  {formatPrice(item.price.amount, item.price.currency)}
                </span>
              </div>
            ))}
          </div>

          <div className="flex justify-between items-center font-bold text-xl pt-4 border-t border-gray-200 dark:border-gray-700">
            <span className="text-gray-900 dark:text-gray-100">Total</span>
            <span className="text-primary-600 dark:text-primary-400">
              {getFormattedTotal()}
            </span>
          </div>
        </div>

        {/* Payment Section */}
        <div className="card p-6">
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-6 flex items-center gap-2">
            <CreditCard size={20} />
            Payment Details
          </h2>

          {loading && (
            <div className="text-center py-12">
              <RefreshCw size={40} className="animate-spin text-primary-600 dark:text-primary-400 mx-auto mb-4" />
              <p className="text-gray-600 dark:text-gray-400">Initializing secure payment...</p>
            </div>
          )}

          {error && !loading && (
            <div className="p-4 rounded-xl flex items-start gap-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 mb-6">
              <AlertCircle size={20} className="text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="font-medium text-red-800 dark:text-red-200">Payment Error</p>
                <p className="text-sm text-red-700 dark:text-red-300 mt-1">{error}</p>
                <button
                  onClick={() => window.location.reload()}
                  className="mt-3 btn btn-secondary text-sm inline-flex items-center gap-2"
                >
                  <RefreshCw size={14} />
                  Try Again
                </button>
              </div>
            </div>
          )}

          {clientSecret && !loading && stripePromise && currentUser && (
            <Elements stripe={stripePromise} options={elementsOptions}>
              <PaymentForm
                onSuccess={handlePaymentSuccess}
                onError={handlePaymentError}
                amount={total}
                currency={stripeCurrency}
                userEmail={currentUser.email || ''}
                userName={userData?.displayName || currentUser.displayName || ''}
              />
            </Elements>
          )}

          {/* What You'll Get */}
          <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              After purchase you'll receive:
            </h3>
            <ul className="space-y-2">
              <li className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                <CheckCircle size={16} className="text-green-500" />
                Instant access to your digital downloads
              </li>
              <li className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                <CheckCircle size={16} className="text-green-500" />
                Email confirmation with order details
              </li>
              <li className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                <CheckCircle size={16} className="text-green-500" />
                Access from your account dashboard
              </li>
            </ul>
          </div>
        </div>

        {/* Legal Disclaimer */}
        <LegalDisclaimer variant="prominent" className="mt-6" />

        {/* Help CTA */}
        <section className="mt-8 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-750 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white dark:bg-gray-700 rounded-full shadow-sm">
                <HelpCircle size={24} className="text-gray-600 dark:text-gray-400" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 dark:text-gray-100">
                  Need help with checkout?
                </h4>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Visit our help center for payment FAQs
                </p>
              </div>
            </div>
            <Link to="/help" className="btn btn-secondary whitespace-nowrap">
              Get Help
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
}
